import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Heart, ExternalLink } from 'lucide-react';
import logoMark from '@/assets/logo.svg'; // Updated path to use the main logo

/**
 * Modern, sleek footer component for the Meduler application
 * Features a minimalistic design with all necessary links and information
 * Responsive layout that adapts to different screen sizes
 */

export function Footer() {
  const { toast } = useToast();
  
  // Handle update check
  const checkForUpdates = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new Event('updateServiceWorker'));
    toast({
      title: "Checking for updates...",
      description: "Looking for the latest version of Meduler."
    });
  };
  
  return (
    <footer className="mt-auto w-full bg-gradient-to-b from-white to-gray-50 py-6 border-t border-gray-100 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 py-2">
          {/* Logo and Tagline */}
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-2">
              <img src={logoMark} alt="Meduler Logo" className="h-8 w-8" data-component-name="Footer" />
              <h3 className="text-base font-semibold text-gray-800 tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-700">Meduler</h3>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Your Intelligent Virtual Pillbox
            </p>
          </div>
          
          {/* Quick Links */}
          <nav aria-label="Footer Navigation">
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm items-center md:items-start">
              <li>
                <Link to="/contact" className="text-gray-500 hover:text-purple-600 transition-colors duration-200">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-500 hover:text-purple-600 transition-colors duration-200">
                  Privacy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-500 hover:text-purple-600 transition-colors duration-200">
                  Terms
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-gray-500 hover:text-purple-600 transition-colors duration-200">
                  Disclaimer
                </Link>
              </li>
              <li>
                <a 
                  href="mailto:support@meduler.com" 
                  className="text-gray-500 hover:text-purple-600 transition-colors duration-200 inline-flex items-center gap-1 justify-start"
                  aria-label="Email Support"
                  data-component-name="Footer"
                >
                  <span className="inline-block">Support</span>
                  <ExternalLink className="h-3 w-3 inline-block flex-shrink-0" />
                </a>
              </li>
            </ul>
          </nav>
        </div>
        
        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-purple-200 to-transparent my-5"></div>
        
        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row justify-between items-center text-xs text-gray-400 py-2">
          <div>
            © 2025 Meduler. All rights reserved.
          </div>
          <div className="flex items-center mt-3 md:mt-0 gap-4">
            <div className="flex items-center">
              <span>Made with</span>
              <Heart className="h-3 w-3 text-red-400 mx-1" fill="currentColor" />
              <span>for better medication management</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}